import { Routes } from '@angular/router';
import {
  AppTokenGuard,
  AutoLoginGuard,
} from '@gfc-flash/guard-authentification';
import config from '../../capacitor.config';
import {
  ClientAppErrorStatusTitleEnum,
  ClientAuthentificationErrorStatusTitleEnum,
  ClientLegalErrorStatusTitleEnum,
  ClientNetworkErrorStatusTitleEnum,
  ClientOtherErrorStatusTitleEnum,
  HttpClientErrorStatusTitleEnum,
  HttpServerErrorStatusTitleEnum,
} from '@gfc-flash/package-logger';
import { ErrorModelType } from '@gfc-flash/page-error';
import { SplashscreenModelType } from '@gfc-flash/page-splashscreen';
import { PlatformRedirectGuard } from '@gfc-flash/guard-platform-redirect';

/**
 * @description Error page route definitions mapped to shared GFC error model identifiers.
 */
const errorRoutes: Routes = [
  {
    path: '0',
    title: HttpServerErrorStatusTitleEnum.SERVER_NOT_FOUND,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '0' as ErrorModelType,
    },
  },
  {
    path: '400',
    title: HttpClientErrorStatusTitleEnum.BAD_REQUEST,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '400' as ErrorModelType,
    },
  },
  {
    path: '401',
    title: HttpClientErrorStatusTitleEnum.UNAUTHORIZED,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '401' as ErrorModelType,
    },
  },
  {
    path: '404',
    title: HttpClientErrorStatusTitleEnum.NOT_FOUND,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '404' as ErrorModelType,
    },
  },
  {
    path: '500',
    title: HttpServerErrorStatusTitleEnum.INTERNAL_SERVER_ERROR,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '500' as ErrorModelType,
    },
  },
  {
    path: '503',
    title: HttpServerErrorStatusTitleEnum.SERVICE_UNAVAILABLE,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '503' as ErrorModelType,
    },
  },
  {
    path: '504',
    title: HttpServerErrorStatusTitleEnum.GATEWAY_TIMEOUT,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '504' as ErrorModelType,
    },
  },
  {
    path: '599',
    title: HttpServerErrorStatusTitleEnum.MAINTENANCE_AVAILABLE,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '599' as ErrorModelType,
    },
  },
  {
    path: '1407',
    title: ClientAppErrorStatusTitleEnum.APP_UPDATE_REQUIRED,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '1407' as ErrorModelType,
    },
  },
  {
    path: '2405',
    title:
      ClientAuthentificationErrorStatusTitleEnum.AUTHENTIFICATION_SESSION_EXPIRED,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '2405' as ErrorModelType,
    },
  },
  {
    path: '2409',
    title:
      ClientAuthentificationErrorStatusTitleEnum.AUTHENTIFICATION_SESSION_IDLE,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '2409' as ErrorModelType,
    },
  },
  {
    path: '6405',
    title: ClientLegalErrorStatusTitleEnum.LEGAL_UPDATE_CONSENT_REQUIRE,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '6405' as ErrorModelType,
    },
  },
  {
    path: '7415',
    title: ClientNetworkErrorStatusTitleEnum.NETWORK_TIMEOUT_ERROR,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '7415' as ErrorModelType,
    },
  },
  {
    path: '99499',
    title: ClientOtherErrorStatusTitleEnum.OTHER_UNKNOWN_ERROR,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '99499' as ErrorModelType,
    },
  },
  {
    path: '**',
    title: HttpClientErrorStatusTitleEnum.NOT_FOUND,
    loadComponent: () =>
      import('@gfc-flash/page-error').then((m) => m.ErrorPage),
    data: {
      modelType: '404' as ErrorModelType,
    },
  },
];

/**
 * @description Standalone application route tree with authentication guards and lazy Ionic pages.
 */
export const routes: Routes = [
  {
    path: '',
    title: config.appName,
    canActivateChild: [AppTokenGuard],
    children: [
      {
        path: '',
        redirectTo: 'splashscreen',
        pathMatch: 'full',
      },
      {
        path: 'splashscreen',
        loadComponent: () =>
          import('@gfc-flash/page-splashscreen').then(
            (m) => m.SplashscreenPage,
          ),
        canActivate: [PlatformRedirectGuard],
        data: {
          modelType: 'default' as SplashscreenModelType,
          redirect: 'login',
        },
      },
      {
        path: 'login',
        title: config.appName,
        canActivate: [AutoLoginGuard],
        data: {
          isLogin: 'login',
          redirectHome: 'home',
        },
        loadComponent: () =>
          import('@gfc-flash/page-login').then((m) => m.LoginPage),
      },
      {
        path: 'register',
        title: config.appName,
        loadComponent: () =>
          import('@gfc-flash/page-register').then((m) => m.RegisterPage),
      },
      {
        path: 'home',
        title: config.appName,
        canActivate: [AutoLoginGuard],
        data: {
          redirectLogin: 'login',
        },
        loadComponent: () =>
          import('./pages/home/home.page').then((m) => m.HomePage),
      },
    ],
  },
  ...errorRoutes,
];
